import { useTranslation } from "react-i18next";
import { Sparkles, Youtube, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import youtubeLogo from "@packages/assets/youtube.png";
import driveLogo from "@packages/assets/googledrive.png";
import { CreateHomeworkAssignmentRequestBodyGenerator } from "@packages/api/madlenAPI.schemas";
import { useLocalStorage } from "@packages/hooks/useLocalStorage";
import { Button, Text } from "../../../../core";
import { trackEvent } from "@packages/hooks/useAnalytics";

interface GenerationOption {
  type: CreateHomeworkAssignmentRequestBodyGenerator;
  title: string;
  description: string;
  imageSrc: string;
  icon: React.ReactNode;
}

const AssignmentGeneratorSelectionView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [hoveredOption, setHoveredOption] =
    useState<CreateHomeworkAssignmentRequestBodyGenerator | null>(null);
  const { trackEvent: track } = trackEvent();

  const [, setIsClickedBefore] = useLocalStorage("generator-clicked-before", {
    PROMPT: false,
    TOPIC_LIST: false,
    DRIVE_FILE: false,
    YOUTUBE_LINK: false,
    LEARNING_OUTCOME: false,
  });

  const generationOptions: GenerationOption[] = [
    {
      type: "YOUTUBE_LINK",
      title: t("assignments.generator.youtube.title"),
      description: t("assignments.generator.youtube.description"),
      imageSrc: youtubeLogo,
      icon: <Youtube className="w-6 h-6" />,
    },
    {
      type: "DRIVE_FILE",
      title: t("assignments.generator.file.title"),
      description: t("assignments.generator.file.description"),
      imageSrc: driveLogo,
      icon: <FileText className="w-6 h-6" />,
    },
  ];

  const handleClick = (option: GenerationOption) => {
    track(
      {
        category: "ContentGeneration",
        action: "assignment.generate",
        label: "Assignment generation click",
        nonInteraction: false,
      },
      {
        location: "/assignment/generate",
        element: "generate-button",
        assignmentGenerationType: option.type,
      },
    );
    handleGenerationTypeSelect(option.type);
  };

  const handleGenerationTypeSelect = (
    type: CreateHomeworkAssignmentRequestBodyGenerator,
  ) => {
    setIsClickedBefore((prev) => ({
      ...prev,
      [type]: true,
    }));
    navigate(`/assignments/generate/${type.toLowerCase()}`);
  };

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Header Section - Same as AllAssignmentsHeader */}
        <div className="flex flex-col md:flex-row justify-between items-start mb-4 md:mb-8">
          <div className="">
            <Text variant="h1">assignments.generator.title</Text>
            <Text variant="p" className="text-gray-600 dark:text-gray-400 mt-1">
              assignments.generator.subtitle
            </Text>
          </div>
        </div>

        {/* Generation Options Grid - Same design as AllAssignmentsHeader */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {generationOptions.map((option) => {
            const isHovered = hoveredOption === option.type;

            return (
              <button
                key={option.type}
                onClick={() => handleClick(option)}
                onMouseEnter={() => setHoveredOption(option.type)}
                onMouseLeave={() => setHoveredOption(null)}
                className={`
                  relative w-full p-8 rounded-2xl border border-gray-200 dark:border-dark-400
                  bg-white dark:bg-dark-900 hover:bg-gray-50 dark:hover:bg-dark-600 
                  transition-all duration-300 ease-out
                  group overflow-hidden cursor-pointer
                  ${isHovered ? "scale-[1.01] shadow-xl border-gray-300 dark:border-dark-300" : "shadow-sm hover:shadow-lg"}
                `}
              >
                {/* Content */}
                <div className="relative z-10 flex items-start space-x-6">
                  {/* Logo/Icon */}
                  <div className="flex-shrink-0">
                    <img
                      src={option.imageSrc}
                      alt={option.title}
                      className="w-16 h-16 rounded-2xl object-contain bg-gray-50 dark:bg-dark-400 p-3 shadow-sm group-hover:shadow-md transition-shadow duration-300"
                    />
                  </div>

                  {/* Text content */}
                  <div className="flex-1 min-w-0 text-left">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 leading-tight text-left">
                      {option.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed text-left">
                      {option.description}
                    </p>
                  </div>

                  {/* Arrow indicator */}
                  <div className="flex-shrink-0 mt-1">
                    <div
                      className={`
                      w-8 h-8 rounded-full bg-gray-100 dark:bg-dark-400 
                      flex items-center justify-center
                      transition-all duration-300 ease-out
                      ${isHovered ? "bg-gray-200 dark:bg-dark-300 scale-110" : ""}
                    `}
                    >
                      <svg
                        className={`w-4 h-4 text-gray-600 dark:text-gray-300 transition-transform duration-300 ${
                          isHovered ? "translate-x-0.5" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Subtle glow effect on hover */}
                <div
                  className={`
                  absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 via-purple-500/0 to-pink-500/0
                  transition-all duration-300
                  ${isHovered ? "from-blue-500/5 via-purple-500/5 to-pink-500/5" : ""}
                `}
                />
              </button>
            );
          })}
        </div>

        {/* Additional Info Section */}
        <div className="mt-12 bg-white dark:bg-dark-900 rounded-2xl border border-gray-200 dark:border-dark-400 p-6">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                AI-Powered Assignment Generation
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Choose your preferred method to create engaging assignments. Our
                AI will help you generate high-quality content tailored to your
                students' needs and learning objectives.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentGeneratorSelectionView;
